import { type ContextType, useCallback, useContext } from 'react';
import { useNavigationContainerRef, useRouter } from 'expo-router';

import { LinkingContext } from 'expo-router/react-navigation';

import { useIsFirstAuthAttempt } from '../../../context/AuthMachine';
import { useNavigationReset } from '../../../routing/hooks/useNavigationReset';

// ─────────────────────────────────────────────────────────────────────────────

export const useRedirectAfterAuthStateChange = (params: Params = {}) => {
  const { shouldResetNavigation } = params;

  const router = useRouter();
  const navigationRef = useNavigationContainerRef();
  const resetNavigation = useNavigationReset();
  const { options: linkingOptions } = useContext(LinkingContext);
  const isFirstAuthAttempt = useIsFirstAuthAttempt();

  return useCallback(
    (href: string | undefined) => {
      const target = checkIfSafeInternalHref(href) ? href : '/';
      const navigationOptions = { withAnchor: target.startsWith(ACCOUNT_PATH_PREFIX) };

      // NOTE: Cold start has no stale stack to clear, so we navigate directly.
      if (isFirstAuthAttempt) {
        router.navigate(target as never, navigationOptions);

        return;
      }

      // NOTE: Customers who were already signed in have no pre-login screens to clear.
      if (!shouldResetNavigation) {
        router.dismissTo(target as never, navigationOptions);

        return;
      }

      /**
       * Mid-session login (signed-out → signed-in): replace the whole
       * navigation state with fresh stacks that end on the target, in a single
       * action, so neither the auth screens nor pre-login screens are left
       * behind.
       *
       * NOTE: A reset followed by a separate navigate can race, and `replace` or
       *       `navigate` from inside the auth modal mount a second tab tree.
       *       Both crash react-native-screens on iOS.
       */
      const linkedState = linkingOptions?.getStateFromPath?.(target, linkingOptions.config);
      const resetState = getResetState({ linkedState, rootState: navigationRef.getRootState() });

      if (!resetState) {
        router.dismissTo(target as never, navigationOptions);

        return;
      }

      resetNavigation(resetState as never);
    },
    [
      router,
      navigationRef,
      resetNavigation,
      linkingOptions,
      isFirstAuthAttempt,
      shouldResetNavigation,
    ],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfSafeInternalHref(href: string | undefined): href is string {
  if (typeof href !== 'string' || href.length === 0) return false;

  // Must start with a single `/` — `//` is a protocol-relative URL.
  return href.startsWith('/') && !href.startsWith('//');
}

/**
 * Linked states start at expo-router's root slot, which renders the root
 * layout. The reset keeps the slot's current key so the app doesn't remount,
 * and drops its params: a deep link opened while the app is running leaves its
 * target there, and every later reset would restore that screen.
 */
function getResetState(params: GetResetStateParams) {
  const { linkedState, rootState } = params;

  const linkedRootRoute = linkedState?.routes[0];
  const currentRootRoute = rootState?.routes[0];

  if (linkedRootRoute?.name !== ROOT_SLOT_ROUTE_NAME) {
    return;
  }

  if (currentRootRoute?.name !== ROOT_SLOT_ROUTE_NAME) {
    return;
  }

  return {
    ...rootState,
    index: 0,
    routes: [
      { key: currentRootRoute.key, name: ROOT_SLOT_ROUTE_NAME, state: linkedRootRoute.state },
    ],
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCOUNT_PATH_PREFIX = '/account/';
const ROOT_SLOT_ROUTE_NAME = '__root';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  /**
   * Replaces the whole navigation with fresh stacks ending on the target.
   * Only for customers who just signed in.
   */
  shouldResetNavigation?: boolean;
};

type GetResetStateParams = {
  linkedState: ReturnType<NonNullable<LinkingOptions['getStateFromPath']>>;
  rootState: ReturnType<ReturnType<typeof useNavigationContainerRef>['getRootState']> | undefined;
};

type LinkingOptions = NonNullable<ContextType<typeof LinkingContext>['options']>;
